// TabColors.jsx - World-Class Theme & Color Suite for MeMark
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FONT_TARGETS, THEME_PRESETS } from './settingsConstants';
import { playSound } from '../../lib/soundEffects';

const { FiCheck, FiMoon, FiSun, FiRefreshCw, FiZap, FiSliders, FiPalette } = FiIcons;

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function normalizeHex(v) {
  if (typeof v !== 'string') return '';
  return v.trim().toUpperCase();
}

function isHex(v) {
  return HEX_RE.test((v || '').trim());
}

function SectionLabel({ children, mutedText }) {
  return (
    <p
      className="text-[10px] font-mono uppercase tracking-widest mb-3 flex items-center gap-1.5 font-bold"
      style={{ color: mutedText }}
    >
      <span>{children}</span>
    </p>
  );
}

const QUICK_ACCENTS = [
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Cyan', hex: '#00F0FF' },
  { name: 'Amber', hex: '#F97316' },
  { name: 'Violet', hex: '#A855F7' },
  { name: 'Rose', hex: '#F43F5E' },
  { name: 'Sky', hex: '#38BDF8' },
  { name: 'Monochrome', hex: '#FAFAFA' },
  { name: 'Pine', hex: '#059669' },
];

export default function TabColors({
  settings,
  onApplyChange,
  onFontColorsApply,
  theme,
  isDark,
}) {
  const { textColor, mutedText, inputBg, inputBorder, cardBg, modalBorder, surfaceBg } = theme;
  const currentColors = settings.themeColors || {};
  const currentThemeId = settings.theme || 'neo_emerald';

  const [activeTarget, setActiveTarget] = useState('accent');
  const [hexInput, setHexInput] = useState(currentColors.accent || '#10B981');
  const [hexError, setHexError] = useState(false);

  useEffect(() => {
    setHexInput(currentColors[activeTarget] || '#10B981');
    setHexError(false);
  }, [activeTarget, currentColors]);

  const activeTargetData = useMemo(
    () => FONT_TARGETS.find((t) => t.key === activeTarget),
    [activeTarget]
  );

  const handleSelectPreset = (preset) => {
    playSound('chime');
    onApplyChange({
      theme: preset.id,
      isDark: preset.mode === 'dark',
      themeColors: { ...preset.colors },
    });
  };

  const handleToggleMode = (mode) => {
    playSound('click');
    const makeDark = mode === 'dark';
    if (makeDark === isDark) return;

    // Pick a matching default preset for the target mode
    const targetPreset = makeDark
      ? THEME_PRESETS.find((p) => p.id === 'neo_emerald') || THEME_PRESETS[0]
      : THEME_PRESETS.find((p) => p.id === 'nordic_alabaster') || THEME_PRESETS[6];

    onApplyChange({
      theme: targetPreset.id,
      isDark: makeDark,
      themeColors: { ...targetPreset.colors },
    });
  };

  const handleQuickAccent = (accentHex) => {
    playSound('click');
    onApplyChange({
      accent: accentHex,
      customAccent: accentHex,
    });
  };

  const handleHexApply = () => {
    const v = normalizeHex(hexInput);
    if (!isHex(v)) {
      setHexError(true);
      return;
    }
    setHexError(false);
    playSound('click');
    onFontColorsApply({ ...currentColors, [activeTarget]: v }, true);
  };

  const handleReset = () => {
    playSound('click');
    const defaultPreset = THEME_PRESETS[0];
    onApplyChange({
      theme: defaultPreset.id,
      isDark: true,
      themeColors: { ...defaultPreset.colors },
    });
  };

  return (
    <div className="space-y-8">
      {/* 1. Mode Switcher (Dark / Light) */}
      <div>
        <SectionLabel mutedText={mutedText}>Display Appearance</SectionLabel>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <button
            type="button"
            onClick={() => handleToggleMode('dark')}
            className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between cursor-pointer ${
              isDark
                ? 'border-emerald-500/50 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.15)]'
                : 'border-slate-200 hover:border-slate-300 bg-white shadow-xs'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiMoon} className="w-4 h-4" />
              </div>
              <div className="text-left">
                <p className="text-xs font-bold" style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>Dark Mode</p>
                <p className="text-[11px]" style={{ color: isDark ? '#94A3B8' : '#64748B' }}>Obsidian Cyber</p>
              </div>
            </div>
            {isDark && (
              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-black font-bold">
                <SafeIcon icon={FiCheck} className="w-3 h-3" />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => handleToggleMode('light')}
            className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between cursor-pointer ${
              !isDark
                ? 'border-emerald-500/50 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.15)]'
                : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-500">
                <SafeIcon icon={FiSun} className="w-4 h-4" />
              </div>
              <div className="text-left">
                <p className="text-xs font-bold" style={{ color: isDark ? '#FFFFFF' : '#0F172A' }}>Light Mode</p>
                <p className="text-[11px]" style={{ color: isDark ? '#94A3B8' : '#64748B' }}>Pure Daylight</p>
              </div>
            </div>
            {!isDark && (
              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-black font-bold">
                <SafeIcon icon={FiCheck} className="w-3 h-3" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* 2. New-Age Curated Theme Presets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel mutedText={mutedText}>Curated Atmosphere Themes</SectionLabel>
          <span className="text-[10px] font-mono text-emerald-400/80">10 New-Age Presets</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {THEME_PRESETS.map((p) => {
            const isSelected = currentThemeId === p.id;
            return (
              <motion.button
                key={p.id}
                type="button"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleSelectPreset(p)}
                className={`p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between min-h-[92px] ${
                  isSelected
                    ? 'border-emerald-500 shadow-[0_0_25px_rgba(16,185,129,0.2)]'
                    : isDark
                    ? 'border-white/10 hover:border-white/25'
                    : 'border-slate-200 hover:border-slate-300 shadow-xs'
                }`}
                style={{
                  backgroundColor: p.previewColors[0],
                }}
              >
                <div className="flex items-start justify-between w-full mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-bold"
                        style={{ color: p.mode === 'dark' ? '#F8FAFC' : '#0F172A' }}
                      >
                        {p.name}
                      </span>
                      <span
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded-full uppercase"
                        style={{
                          backgroundColor: `${p.accent}25`,
                          color: p.accent,
                          border: `1px solid ${p.accent}40`,
                        }}
                      >
                        {p.mode}
                      </span>
                    </div>
                    <p
                      className="text-[11px] opacity-75 mt-0.5"
                      style={{ color: p.mode === 'dark' ? '#94A3B8' : '#64748B' }}
                    >
                      {p.tagline}
                    </p>
                  </div>

                  {isSelected && (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-sm"
                      style={{ backgroundColor: p.accent, color: '#000000' }}
                    >
                      <SafeIcon icon={FiCheck} className="w-3 h-3 font-extrabold" />
                    </div>
                  )}
                </div>

                {/* Color Swatches Palette */}
                <div className="flex items-center gap-1.5 mt-2">
                  {p.previewColors.map((hex, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full border border-black/30 shadow-xs"
                      style={{ backgroundColor: hex }}
                      title={hex}
                    />
                  ))}
                  <span
                    className="text-[10px] font-mono ml-auto"
                    style={{ color: p.accent }}
                  >
                    {p.accent}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* 3. Quick Accent Chips */}
      <div>
        <SectionLabel mutedText={mutedText}>Quick Accent Highlights</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {QUICK_ACCENTS.map((acc) => {
            const isAccActive = (currentColors.accent || '').toUpperCase() === acc.hex;
            return (
              <button
                key={acc.name}
                type="button"
                onClick={() => handleQuickAccent(acc.hex)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all cursor-pointer ${
                  isAccActive
                    ? isDark
                      ? 'border-emerald-400 bg-white/10 text-white font-bold shadow-sm'
                      : 'border-emerald-500 bg-emerald-500/15 text-slate-900 font-bold shadow-sm'
                    : isDark
                    ? 'border-white/10 text-zinc-400 hover:text-white hover:bg-white/5'
                    : 'border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <div
                  className="w-3.5 h-3.5 rounded-full border border-white/20"
                  style={{ backgroundColor: acc.hex }}
                />
                <span>{acc.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Custom Fine-Tuning Token Selector & Hex Input */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel mutedText={mutedText}>Power User Color Tokens (Hex Only)</SectionLabel>
          <button
            type="button"
            onClick={handleReset}
            className="text-[11px] text-zinc-400 hover:text-emerald-400 flex items-center gap-1 transition-colors cursor-pointer"
          >
            <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
            <span>Reset to Neo Emerald</span>
          </button>
        </div>

        <div className="flex gap-1.5 flex-wrap mb-4">
          {FONT_TARGETS.map((t) => {
            const isActive = activeTarget === t.key;
            const accent = currentColors.accent || '#10B981';

            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTarget(t.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all border cursor-pointer"
                style={{
                  borderColor: isActive ? accent : modalBorder,
                  backgroundColor: isActive ? `${accent}25` : cardBg,
                  color: isActive ? textColor : mutedText,
                }}
              >
                <div
                  className="w-3 h-3 rounded-full border border-white/20"
                  style={{ backgroundColor: currentColors[t.key] || '#10B981' }}
                />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {activeTargetData && (
          <div
            className="p-4 rounded-2xl border space-y-3"
            style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold" style={{ color: textColor }}>
                  {activeTargetData.label} Token
                </p>
                <p className="text-[11px]" style={{ color: mutedText }}>
                  {activeTargetData.desc}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={(currentColors[activeTarget] || '#10B981').slice(0, 7)}
                  onChange={(e) => {
                    const val = normalizeHex(e.target.value);
                    setHexInput(val);
                    onFontColorsApply({ ...currentColors, [activeTarget]: val }, true);
                  }}
                  className="w-8 h-8 rounded-lg cursor-pointer border-0 p-0 overflow-hidden bg-transparent"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value.toUpperCase())}
                onBlur={handleHexApply}
                placeholder="#10B981 or #RRGGBBAA"
                className="flex-1 rounded-xl px-3 py-2 text-xs font-mono border focus:outline-none transition-all"
                style={{
                  backgroundColor: inputBg,
                  borderColor: hexError ? '#EF4444' : inputBorder,
                  color: textColor,
                }}
              />
              <button
                type="button"
                onClick={handleHexApply}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all cursor-pointer"
              >
                Apply
              </button>
            </div>
            {hexError && (
              <p className="text-xs text-red-400">
                Invalid hex code. Please enter #RGB, #RRGGBB, or #RRGGBBAA.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}